import React, { Suspense, useEffect, useCallback } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { App as AntApp } from 'antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useSetAtom } from 'jotai'

import AppRoutes from './routes'
import Loading from '@/components/Loading'
import { LanguageStorageSync } from '@/i18n/languageStorageSync'
import { dirtyModelAtom, logoFileAtom } from '@/libs/context/organization/organizationAtoms'
import { useAuth } from '@/libs/context/auth'
import { AuthProvider } from '@/libs/context/auth'
import { ThemeProvider } from '@/libs/theme'
import { isSentryEnabled, Sentry } from '@/libs/sentry'
import { QUERY_CLIENT_CONFIG } from '@/config/reactQuery'

const queryClient = new QueryClient(QUERY_CLIENT_CONFIG)
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
})
persistQueryClient({
  queryClient,
  persister: localStoragePersister,
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
})

function AppRouterContent() {
  const { user } = useAuth()

  useEffect(() => {
    if (isSentryEnabled()) {
      if (user) {
        Sentry.setUser({ id: user.id, email: user.email })
      } else {
        Sentry.setUser(null)
      }
    }
  }, [user])

  return (
    <Suspense fallback={<Loading />}>
      <AppRoutes />
    </Suspense>
  )
}

function AppWithTheme() {
  const setDirtyModel = useSetAtom(dirtyModelAtom)
  const setLogoFile = useSetAtom(logoFileAtom)
  const onLogout = useCallback(() => {
    setDirtyModel({})
    setLogoFile(null)
  }, [setDirtyModel, setLogoFile])

  return (
    <AntApp>
      <AuthProvider onLogout={onLogout}>
        <AppRouterContent />
      </AuthProvider>
    </AntApp>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AntApp>
            <LanguageStorageSync />
            <AppWithTheme />
          </AntApp>
        </ThemeProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
